import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./InputDropdown.scss";
import { FaChevronDown } from "react-icons/fa";
import InputCheckbox from "../../common/Inputs/InputCheckbox/InputCheckbox";
import { IoClose } from "react-icons/io5";
import { Option } from "../../../types/types";

const ChevronDownIcon = FaChevronDown as React.FC;
const CloseIcon = IoClose as React.FC;

type InputDropdownProps = {
    label?: string;
    labelVisible?: boolean;
    options: Option[];
    value?: Option | Option[] | null;
    onChange?: (value: Option | Option[] | null) => void;
    placeholder?: string;
    defaultValue?: Option | null;
    disabled?: boolean;
    withCheckbox?: boolean;
    isMultiple?: boolean;
    clearable?: boolean;
    tableMode?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
};

const InputDropdown: React.FC<InputDropdownProps> = ({
    label,
    labelVisible = true,
    options,
    value,
    onChange,
    placeholder = "Selecciona una opción",
    defaultValue,
    disabled = false,
    withCheckbox = false,
    isMultiple = false,
    clearable = false,
    tableMode = false,
    onOpenChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<Option | null>(
        defaultValue || null
    );
    const [selectedValues, setSelectedValues] = useState<Option[]>([]);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const controlRef = useRef<HTMLDivElement>(null);

    // Inicializar valores basándose en las props
    useEffect(() => {
        if (value !== undefined) {
            if (isMultiple || withCheckbox) {
                setSelectedValues(
                    Array.isArray(value) ? value : value ? [value] : []
                );
            } else {
                setSelectedValue(
                    Array.isArray(value) ? value[0] || null : value
                );
            }
        } else if (defaultValue && !isMultiple && !withCheckbox) {
            setSelectedValue(defaultValue);
        }
    }, [value, defaultValue, isMultiple, withCheckbox]);

    useEffect(() => {
        onOpenChange?.(isOpen);

        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onOpenChange]);

    const handleOptionClick = (option: Option) => {
        if (isMultiple || withCheckbox) {
            const isSelected = selectedValues.some(
                (v) => v.value === option.value
            );
            let newSelectedValues: Option[];

            if (isSelected) {
                newSelectedValues = selectedValues.filter(
                    (v) => v.value !== option.value
                );
            } else {
                newSelectedValues = [...selectedValues, option];
            }

            setSelectedValues(newSelectedValues);
            onChange?.(newSelectedValues);
        } else {
            setSelectedValue(option);
            onChange?.(option);
            setIsOpen(false);
        }
    };

    const handleReset = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (isMultiple || withCheckbox) {
            setSelectedValues([]);
            onChange?.([]);
        } else {
            setSelectedValue(null);
            onChange?.(null);
        }
    };

    const handleRemoveSelectedValue = (
        event: React.MouseEvent,
        optionToRemove: Option
    ) => {
        event.stopPropagation();
        const newSelectedValues = selectedValues.filter(
            (v) => v.value !== optionToRemove.value
        );
        setSelectedValues(newSelectedValues);
        onChange?.(newSelectedValues);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            const newIsOpen = !isOpen;

            if (tableMode && controlRef.current) {
                const rect = controlRef.current.getBoundingClientRect();
                setPosition({
                    top: rect.bottom + window.scrollY + 2,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                });
            }
            setIsOpen(newIsOpen);
        }
    };

    const renderDropdownMenu = () => {
        if (!isOpen) return null;

        if (tableMode) {
            const menuContent = (
                <div
                    style={{
                        position: "fixed",
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        width: `${position.width}px`,
                        zIndex: 2147483647,
                        background: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        maxHeight: "200px",
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                    }}
                >
                    {options.map((option) => (
                        <div
                            key={option.value}
                            onClick={() => handleOptionClick(option)}
                            style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f3f4f6",
                                fontSize: "14px",
                                color: "black",
                            }}
                            onMouseEnter={(e) => {
                                const target = e.target as HTMLDivElement;
                                target.style.backgroundColor = "#f9fafb";
                            }}
                            onMouseLeave={(e) => {
                                const target = e.target as HTMLDivElement;
                                target.style.backgroundColor = "white";
                            }}
                        >
                            {isMultiple || withCheckbox ? (
                                <InputCheckbox
                                    id={option.value}
                                    label={option.label}
                                    checked={selectedValues.some(
                                        (v) => v.value === option.value
                                    )}
                                    onChange={() => handleOptionClick(option)}
                                />
                            ) : (
                                option.label
                            )}
                        </div>
                    ))}
                </div>
            );

            return ReactDOM.createPortal(menuContent, document.body);
        }

        // Modo normal
        return (
            <ul className="input-dropdown__menu">
                {options.map((option) => (
                    <li
                        key={option.value}
                        className="input-dropdown__option"
                        onClick={() => handleOptionClick(option)}
                    >
                        {isMultiple || withCheckbox ? (
                            <InputCheckbox
                                id={option.value}
                                label={option.label}
                                checked={selectedValues.some(
                                    (v) => v.value === option.value
                                )}
                                onChange={() => handleOptionClick(option)}
                            />
                        ) : (
                            option.label
                        )}
                    </li>
                ))}
            </ul>
        );
    };

    const renderValue = () => {
        if (isMultiple || withCheckbox) {
            if (selectedValues.length === 0) {
                return (
                    <span className="input-dropdown__placeholder">
                        {placeholder}
                    </span>
                );
            }

            // Si hay muchos elementos, mostrar un resumen en lugar de todas las badges
            if (selectedValues.length > 2) {
                return (
                    <div className="input-dropdown__summary">
                        <span className="input-dropdown__summary-text">
                            {selectedValues.length} residuos seleccionados
                        </span>
                        {selectedValues.length > 0 && (
                            <span className="input-dropdown__summary-preview">
                                (
                                {selectedValues
                                    .slice(0, 2)
                                    .map((v) => v.label)
                                    .join(", ")}
                                {selectedValues.length > 2 ? "..." : ""})
                            </span>
                        )}
                    </div>
                );
            }

            return (
                <div className="input-dropdown__selected-values">
                    {selectedValues.map((option) => (
                        <span
                            key={option.value}
                            className="input-dropdown__badge"
                        >
                            {option.label}
                            <span
                                className="input-dropdown__badge__close"
                                onClick={(e) =>
                                    handleRemoveSelectedValue(e, option)
                                }
                            >
                                <CloseIcon />
                            </span>
                        </span>
                    ))}
                </div>
            );
        } else {
            return (
                <span className="input-dropdown__value">
                    {selectedValue?.label || (
                        <span className="input-dropdown__placeholder">
                            {placeholder}
                        </span>
                    )}
                </span>
            );
        }
    };

    const showClearButton = () => {
        if (!clearable) return false;

        if (isMultiple || withCheckbox) {
            return selectedValues.length > 0;
        } else {
            return selectedValue !== null;
        }
    };

    return (
        <div
            className={`input-dropdown ${
                tableMode ? "input-dropdown--table-mode" : ""
            } ${disabled ? "input-dropdown--disabled" : ""}`}
            ref={dropdownRef}
        >
            {labelVisible && label && (
                <label className="input-dropdown__label">{label}</label>
            )}
            <div
                ref={controlRef}
                className={`input-dropdown__control ${
                    isOpen ? "input-dropdown__control--open" : ""
                } ${
                    (isMultiple || withCheckbox) && selectedValues.length > 0
                        ? "input-dropdown__control--has-values"
                        : ""
                }`}
                onClick={toggleDropdown}
            >
                <div className="input-dropdown__value-container">
                    {renderValue()}
                </div>

                <span className="input-dropdown__arrow">
                    <ChevronDownIcon />
                </span>
                {showClearButton() && (
                    <span
                        className="input-dropdown__clear"
                        onClick={handleReset}
                    >
                        <CloseIcon />
                    </span>
                )}
            </div>
            {renderDropdownMenu()}
        </div>
    );
};

export default InputDropdown;
